import { createFileRoute, Link } from "@tanstack/react-router";
import logoAsset from "@/assets/psy-bloom-logo.png.asset.json";
import nicolleAsset from "@/assets/nicolle-gheorghiu-hero.jpeg.asset.json";
import { useRouteHead } from "@/hooks/use-route-head";

const nicolleFaq = [
  { q: "Este normal să mă doară încă atât de mult după o pierdere?", a: "Da. Durerea nu urmează un calendar fix și nu dispare liniar. Uneori reapare în valuri, chiar și după perioade în care ai simțit că lucrurile s-au stabilizat." },
  { q: "Anxietatea înseamnă că pierd controlul?", a: "Nu. Anxietatea este un răspuns al sistemului tău de protecție. De multe ori apare atunci când mintea încearcă să prevină o nouă pierdere sau o nouă experiență dureroasă." },
  { q: "Este ceva în neregulă cu mine dacă simt atât de intens?", a: "Nu. Sensibilitatea emoțională nu este un defect." },
  { q: "De ce mă atașez atât de mult de oameni?", a: "Atașamentul intens apare frecvent atunci când siguranța emoțională a fost afectată de pierderi, respingere sau experiențe dificile." },
  { q: "Este calculatorul problema principală?", a: "Nu întotdeauna. Uneori calculatorul, jocurile sau alte activități devin refugii sigure în perioade dificile." },
  { q: "Dacă nu am direcție în viață, înseamnă că am eșuat?", a: "Nu. Lipsa clarității într-o anumită perioadă a vieții nu înseamnă eșec." },
  { q: "Trebuie să fiu foarte rău ca să vin la psiholog?", a: "Nu. Nu trebuie să ajungi la limită pentru a cere ajutor." },
  { q: "Cât durează o ședință?", a: "Ședința are o durată de 50 de minute." },
];

const routeHead = {
  meta: [
    { title: "Nicolle Gheorghiu — Psiholog clinician | PSY BLOOM Institute" },
    {
      name: "description",
      content:
        "Nicolle Gheorghiu, psiholog clinician la PSY BLOOM Institute. Sprijin pentru pierdere, anxietate, despărțiri și destabilizare emoțională.",
    },
    { property: "og:title", content: "Nicolle Gheorghiu — Psiholog clinician" },
    {
      property: "og:description",
      content:
        "Atunci când simți că ceva s-a rupt și nu mai găsești aceeași siguranță în viața ta. Terapie pentru claritate și stabilitate emoțională.",
    },
    { property: "og:image", content: nicolleAsset.url },
    { property: "og:url", content: "https://psybloominstitute.ro/nicolle-gheorghiu" },
  ],
  links: [{ rel: "canonical", href: "https://psybloominstitute.ro/nicolle-gheorghiu" }],
  scripts: [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Nicolle Gheorghiu",
        jobTitle: "Psiholog clinician",
        image: nicolleAsset.url,
        url: "https://psybloominstitute.ro/nicolle-gheorghiu",
        worksFor: { "@type": "MedicalBusiness", name: "PSY BLOOM Institute" },
      }),
    },
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: nicolleFaq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    },
  ],
};

export const Route = createFileRoute("/nicolle-gheorghiu")({
  component: NicollePage,
});

function NicollePage() {
  useRouteHead(routeHead);
  return (
    <div className="min-h-screen bg-background">
      {/* HEADER */}
      <header className="w-full border-b border-border/60 bg-background/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-3 shrink-0" aria-label="PSY BLOOM Institute">
            <img src={logoAsset.url} alt="PSY BLOOM Institute" className="h-8 sm:h-9 w-auto" width={180} height={36} />
          </Link>
          <a
            href="https://wa.me/40752211147"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-full bg-primary px-4 sm:px-6 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90 hover:shadow-[var(--shadow-card)] whitespace-nowrap"
          >
            Programează o discuție
          </a>
        </div>
      </header>

      <main>
        {/* HERO */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-surface to-background" aria-hidden="true" />
          <div className="mx-auto max-w-6xl px-6 py-16 md:py-24">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
                <p className="text-sm uppercase tracking-wider text-[#32506c] font-medium font-sans mb-5">
                  Nicolle Gheorghiu · Psiholog clinician
                </p>
                <h1 className="text-4xl md:text-5xl leading-[1.1] mb-6 text-balance">
                  Atunci când simți că ceva s-a rupt și nu mai găsești aceeași siguranță în viața ta
                </h1>
                <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-8">
                  Pierderea, anxietatea, despărțirile, conflictele și perioadele de destabilizare emoțională pot lăsa în urmă mai mult decât tristețe. Pot lăsa senzația că nu mai înțelegi ce ți se întâmplă.
                </p>
                <a
                  href="https://wa.me/40752211147"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-3.5 text-sm font-medium text-primary-foreground transition-all hover:bg-primary/90 hover:shadow-[var(--shadow-card)]"
                >
                  Programează o discuție
                </a>
              </div>
              <div className="order-1 lg:order-2">
                <div className="rounded-3xl overflow-hidden border border-border shadow-[var(--shadow-card)] aspect-[4/5]">
                  <img
                    src={nicolleAsset.url}
                    alt="Nicolle Gheorghiu, psiholog clinician la PSY BLOOM Institute"
                    className="w-full h-full object-cover"
                    width={1000}
                    height={1250}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* INTRO SECTION */}
        <section className="py-20 md:py-28 bg-[#32506c]">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <p className="text-base md:text-xl text-white/90 leading-relaxed">
              Mulți oameni ajung în terapie după ce au încercat o perioadă să gestioneze singuri dificultățile prin care trec. Deși își continuă activitățile și responsabilitățile de zi cu zi, în interior pot simți lipsa stabilității, a direcției sau a siguranței. Terapia oferă un spațiu în care experiențele tale pot fi înțelese cu răbdare, fără judecată și fără presiunea de a găsi imediat răspunsuri.
            </p>
          </div>
        </section>

        {/* SECȚIUNEA 2 – POATE TE REGĂSEȘTI */}
        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-4xl px-6">
            <div className="text-center mb-14 md:mb-20">
              <h2 className="text-3xl md:text-4xl mb-6 text-balance">Uneori anxietatea nu apare din senin</h2>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                Pentru mulți oameni, anxietatea apare după o perioadă dificilă, o pierdere, o relație care s-a încheiat, un conflict important sau o experiență care a schimbat sentimentul de siguranță pe care îl aveau înainte.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              <BlockCard title="Simți că nu mai ai aceeași stabilitate interioară" text="Poate că înainte reușeai să gestionezi lucrurile mai ușor, iar acum simți că orice problemă te afectează mult mai intens." />
              <BlockCard title="Te gândești constant la ce s-ar putea întâmpla" text="Îți faci griji pentru oamenii apropiați, pentru viitor sau pentru posibilitatea de a pierde ceea ce contează pentru tine." />
              <BlockCard title="Te afectează mai mult decât îi afectează pe ceilalți" text="Te întrebi de ce simți atât de intens și de ce anumite experiențe par să lase urme atât de adânci." />
              <BlockCard title="Ai trecut printr-o pierdere sau o despărțire" text="Deși timpul a trecut, încă simți că nu ai reușit să găsești aceeași liniște sau aceeași direcție pe care o aveai înainte." />
              <BlockCard title="Te retragi tot mai mult" text="Poate că petreci mai mult timp singur, eviți anumite situații sau te refugiezi în activități care îți oferă predictibilitate și control." />
              <BlockCard title="Nu mai știi încotro să mergi" text="Te întrebi ce vrei de la viață, de ce ceilalți par mai siguri pe ei și de ce tu simți că ai rămas blocat într-un loc din care nu găsești ieșirea." />
            </div>
          </div>
        </section>

        {/* SECȚIUNEA 3 – TESTIMONIALE */}
        <section className="py-20 md:py-28 bg-[#32506c]">
          <div className="mx-auto max-w-6xl px-6">
            <div className="text-center mb-14 md:mb-20">
              <h2 className="text-3xl md:text-4xl mb-4 text-white bg-[#32506c]">Ce spun oamenii care lucrează cu Nicolle</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-4xl mx-auto">
              <TestimonialCard
                quote="„Fac terapie cu Nicolle de la 12 ani și pot spune că m-a ajutat enorm de-a lungul timpului. A fost mereu alături de mine în momentele dificile și m-a ajutat să depășesc multe obstacole din viața mea. Datorită ei, am simțit mereu ca am pe cineva alături care mă asculta și mă înțelege, oferindu-mi spațiul de care aveam nevoie pentru a mă vindeca. Îi sunt foarte recunoscătoare si o recomand din suflet oricui are nevoie de sprijin, înțelegere si un om cu adevărat implicat.”"
                author="Terapie individuală"
                age=""
              />
              <TestimonialCard
                quote="„Suntem foarte mulțumiți de psihologul fiului nostru,dna Popescu Gheorghiu  Nicolle. Am observat progrese importante in dezvoltarea sa emoțională si in modul in care gestionează anumite situații.Apreciem profesionalismul,răbdarea, empatia si capacitatea de a crea o relație de încredere cu fiul nostru.Recomandam cu căldură serviciile sale.”"
                author="Părinte"
                age=""
              />
            </div>
          </div>
        </section>

        {/* SECȚIUNEA – APARIȚII MEDIA */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-gradient-to-br from-[#0f2438] via-[#1a3a55] to-[#32506c]">
          <div className="absolute inset-0 opacity-[0.08] pointer-events-none" aria-hidden="true" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)", backgroundSize: "60px 60px, 90px 90px" }} />
          <div className="relative mx-auto max-w-5xl px-6">
            <div className="text-center mb-14 md:mb-16">
              <p className="text-xs uppercase tracking-[0.25em] font-medium mb-4 text-white/70">
                ★ Apariții media ★
              </p>
              <h2 className="text-3xl md:text-5xl mb-5 max-w-2xl mx-auto text-white">
                Nicolle în presă
              </h2>
              <p className="text-base md:text-lg leading-relaxed max-w-2xl mx-auto text-white/80">
                Articole și interviuri despre parcursul Nicolle și despre transformările care au adus-o în psihologie.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-5 lg:gap-6 max-w-3xl mx-auto">
              <PressCard
                source="Babilon Art"
                title="Nicolle Popescu — de la actriță la psiholog de succes"
                url="https://babilonart.wordpress.com/2019/04/01/nicolle-popescu-de-la-actrita-la-psiholog-de-succes/"
              />
              <PressCard
                source="Antena 1"
                title="Cum arată și cu ce se ocupă acum actrița Nicole Gheorghiu din rolul Maiei din serialul Lacrimi de iubire"
                url="https://a1.ro/showbiz/vedete/cum-arata-si-cu-ce-se-ocupa-acum-actrita-nicole-gheorghiu-din-rolul-maiei-din-serialul-lacrimi-de-iubire-id1139391.html"
              />
            </div>
          </div>
        </section>

        {/* SECȚIUNEA 4 – CUM TE POATE AJUTA TERAPIA */}
        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-4xl px-6">
            <div className="text-center mb-14 md:mb-20">
              <h2 className="text-3xl md:text-4xl mb-6 text-balance">Nu trebuie să treci singur prin tot ceea ce simți</h2>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
                Terapia nu înseamnă să elimini emoțiile sau să devii o altă persoană. Înseamnă să înțelegi de unde vin reacțiile tale, ce încearcă să protejeze și cum poți construi mai multă siguranță în propria viață.
              </p>
              <p className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#32506c]/10 text-sm font-medium text-[#32506c]">
                Durată ședință — 50 de minute
              </p>
            </div>
            <div className="space-y-4 mb-14">
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto text-center">
                Mulți oameni vin în cabinet convinși că ceva este în neregulă cu ei. De cele mai multe ori, descoperă că ceea ce simt are logică și că reacțiile lor sunt răspunsuri la experiențe care i-au afectat mai profund decât au realizat inițial.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              <PillarCard title="Claritate emoțională" text="Înțelegi mai bine ceea ce simți și de ce anumite situații continuă să te afecteze." />
              <PillarCard title="Mai multă siguranță interioară" text="Înveți să gestionezi anxietatea, frica și nesiguranța fără să te simți controlat de ele." />
              <PillarCard title="Relații mai stabile" text="Construiești relații bazate pe siguranță, limite sănătoase și încredere, nu doar pe frica de a pierde sau de a fi abandonat." />
            </div>
          </div>
        </section>

        {/* SECȚIUNEA 5 – FAQ */}
        <section className="py-20 md:py-28 bg-[#dbe1e8]">
          <div className="mx-auto max-w-3xl px-6">
            <div className="text-center mb-14 md:mb-20">
              <h2 className="text-3xl md:text-4xl mb-4 text-[#32506c]">Întrebări frecvente</h2>
            </div>
            <div className="space-y-4">
              <FaqItem question="Este normal să mă doară încă atât de mult după o pierdere?" answer="Da. Durerea nu urmează un calendar fix și nu dispare liniar. Uneori reapare în valuri, chiar și după perioade în care ai simțit că lucrurile s-au stabilizat." />
              <FaqItem question="Anxietatea înseamnă că pierd controlul?" answer="Nu. Anxietatea este un răspuns al sistemului tău de protecție. De multe ori apare atunci când mintea încearcă să prevină o nouă pierdere sau o nouă experiență dureroasă." />
              <FaqItem question="Este ceva în neregulă cu mine dacă simt atât de intens?" answer="Nu. Sensibilitatea emoțională nu este un defect. Mulți oameni care simt profund ajung să creadă că este ceva greșit la ei, când de fapt au nevoie de mai multă înțelegere și reglare emoțională." />
              <FaqItem question="De ce mă atașez atât de mult de oameni?" answer="Atașamentul intens apare frecvent atunci când siguranța emoțională a fost afectată de pierderi, respingere sau experiențe dificile. Nu este o slăbiciune și poate fi înțeles și lucrat în terapie." />
              <FaqItem question="Este calculatorul problema principală?" answer="Nu întotdeauna. Uneori calculatorul, jocurile sau alte activități devin refugii sigure în perioade dificile. Important este să înțelegem ce încearcă persoana să evite sau să gestioneze prin ele." />
              <FaqItem question="Dacă nu am direcție în viață, înseamnă că am eșuat?" answer="Nu. Lipsa clarității într-o anumită perioadă a vieții nu înseamnă eșec. Pentru mulți adolescenți și adulți aflați în perioade de tranziție, confuzia este o parte firească a procesului de căutare și reconstrucție." />
              <FaqItem question="Trebuie să fiu foarte rău ca să vin la psiholog?" answer="Nu. Nu trebuie să ajungi la limită pentru a cere ajutor. Multe persoane aleg terapia atunci când observă că se simt tot mai copleșite, pierd contactul cu ceea ce le face bine sau nu mai găsesc singure claritatea de care au nevoie." />
              <FaqItem question="Cât durează o ședință?" answer="Ședința are o durată de 50 de minute." />
            </div>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-border bg-[#32506c]">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <nav aria-label="Navigare subsol" className="flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-white/80 mb-8">
            <Link to="/" hash="despre" className="hover:text-white transition-colors">Despre</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            <a href="https://blog.psybloominstitute.ro" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Blog</a>
            <Link to="/politica-de-confidentialitate" className="hover:text-white transition-colors">Politica de confidentialitate</Link>
            <Link to="/politica-cookies" className="hover:text-white transition-colors">Politica cookies</Link>
            <Link to="/termeni-si-conditii" className="hover:text-white transition-colors">Termeni si conditii</Link>
          </nav>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-white/20 text-xs text-white/60">
            <p>© {new Date().getFullYear()} PSY BLOOM Institute</p>
            <p>
              Proiect creat de{" "}
              <a href="https://algoritmii.ro" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Algoritmii.ro
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function BlockCard({ title, text }: { title: string; text: string }) {
  return (
    <div className="bg-card rounded-2xl p-6 md:p-8 border border-border/60 shadow-[var(--shadow-card)]">
      <h3 className="text-lg md:text-xl mb-3">{title}</h3>
      <p className="text-muted-foreground leading-relaxed">{text}</p>
    </div>
  );
}

function TestimonialCard({ quote, author, age }: { quote: string; author: string; age: string }) {
  const initial = author.charAt(0).toUpperCase();
  return (
    <figure className="group relative rounded-2xl p-[1px] bg-gradient-to-br from-white/40 via-white/10 to-transparent shadow-[0_25px_60px_-25px_rgba(0,0,0,0.5)] transition-all hover:-translate-y-1 hover:shadow-[0_35px_80px_-25px_rgba(0,0,0,0.6)]">
      <div className="relative h-full bg-gradient-to-br from-white to-[#f5f3ee] rounded-2xl p-7 md:p-9 flex flex-col overflow-hidden">
        <div className="relative flex items-center gap-2 mb-5">
          <span className="h-px w-8 bg-[#32506c]/40" aria-hidden="true" />
          <svg className="w-5 h-5 text-[#32506c]" fill="currentColor" viewBox="0 0 32 32" aria-hidden="true">
            <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14H8c0-1.1.9-2 2-2V8zm14 0c-3.3 0-6 2.7-6 6v10h10V14h-6c0-1.1.9-2 2-2V8z"/>
          </svg>
        </div>
        <blockquote className="relative text-base leading-relaxed text-foreground/90 italic mb-7 flex-1">{quote}</blockquote>
        <figcaption className="relative flex items-center gap-3 pt-5 border-t border-[#32506c]/15">
          <span aria-hidden="true" className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-[#32506c] to-[#1e3a55] text-white text-sm font-medium shadow-md">{initial}</span>
          <span className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground block leading-tight">{author}</span>
            {age && <span className="text-xs">{age}</span>}
          </span>
        </figcaption>
      </div>
    </figure>
  );
}

function PillarCard({ title, text }: { title: string; text: string }) {
  return (
    <div className="bg-card rounded-2xl p-6 md:p-8 border border-border/60 shadow-[var(--shadow-card)] text-center">
      <h3 className="text-lg md:text-xl mb-3">{title}</h3>
      <p className="text-muted-foreground leading-relaxed">{text}</p>
    </div>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  return (
    <details className="bg-card rounded-2xl border border-border/60 shadow-[var(--shadow-card)] group">
      <summary className="flex items-center justify-between cursor-pointer p-5 md:p-6 text-left select-none">
        <span className="font-medium text-[#32506c] pr-4">{question}</span>
        <span aria-hidden="true" className="text-[#32506c] transition-transform group-open:rotate-180 shrink-0 ml-2">▾</span>
      </summary>
      <div className="px-5 md:px-6 pb-5 md:pb-6 text-[#32506c] leading-relaxed">
        {answer}
      </div>
    </details>
  );
}

function PressCard({ source, title, url }: { source: string; title: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block rounded-2xl overflow-hidden border border-white/15 bg-white/[0.04] backdrop-blur-sm p-6 md:p-7 transition-all hover:bg-white/[0.08] hover:border-white/30 hover:-translate-y-1"
    >
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-[0.2em] text-white/60 font-medium">{source}</span>
        <span className="text-white/50 group-hover:text-white group-hover:translate-x-1 transition-all" aria-hidden="true">→</span>
      </div>
      <h3 className="text-base md:text-lg text-white leading-snug">
        {title}
      </h3>
    </a>
  );
}
